import { ArtifactLogo } from "@/components/ui/artifact-logo";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/useToast";
import { SupabaseWorkspaceUtils } from "@/lib/db/utils/supabase/workspace";
import { supabase } from "@/lib/supabase";
import { getAdminClient } from "@/lib/supabaseAdmin";
import { cn } from "@/lib/utils";
import {
  acceptWorkspaceInvitation,
  declineWorkspaceInvitation,
} from "@/lib/utils/notificationUtils";
import useAuthStore from "@/store/authStore";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Custom SVG Icons
const BellIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="m13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const ChatBubbleIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const ExclamationCircleIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const ExclamationTriangleIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
    <path d="M12 9v4" />
    <path d="m12 17 .01 0" />
  </svg>
);

const InformationCircleIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const PaperAirplaneIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="m3 3 3 9-3 9 19-9Z" />
    <path d="m6 12 13 0" />
  </svg>
);

export const NotificationsDialog = ({ isOpen, onOpenChange }) => {
  const { user, workspaces, setWorkspaces } = useAuthStore();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [receivedNotifications, setReceivedNotifications] = useState([]);
  const [sentInvitations, setSentInvitations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [processingNotificationId, setProcessingNotificationId] =
    useState(null);
  const [isAccepting, setIsAccepting] = useState(null);
  const [isDeclining, setIsDeclining] = useState(null);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch notifications when dialog opens
  useEffect(() => {
    if (isOpen && user?.id) {
      fetchNotifications();
    }
  }, [isOpen, user?.id]);

  // Update filtered notifications when tab changes or when notifications change
  useEffect(() => {
    if (activeTab === "pending") {
      // For pending tab, show both received pending invitations and sent invitations
      const pendingReceived = receivedNotifications.filter(
        (notification) => notification.status === "pending"
      );
      // Combined array with both received and sent pending invitations
      setFilteredNotifications([...pendingReceived, ...sentInvitations]);
    } else if (activeTab === "system") {
      // For system tab, show only system notifications
      const systemNotifications = receivedNotifications.filter(
        (notification) => notification.type === "system"
      );
      setFilteredNotifications(systemNotifications);
    } else {
      // Default to showing all received notifications
      setFilteredNotifications(receivedNotifications);
    }
  }, [activeTab, receivedNotifications, sentInvitations]);

  const fetchNotifications = async () => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      // Fetch received notifications (where user is recipient)
      const { data: receivedData, error: receivedError } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (receivedError) {
        console.error("Error fetching received notifications:", receivedError);
        return;
      }

      // Fetch sent notifications (where user is sender) - Fixed query
      const { data: sentData, error: sentError } = await supabase
        .from("notifications")
        .select("*")
        .eq("sender_id", user.id)
        .eq("type", "workspace_invitation")
        .order("created_at", { ascending: false });

      if (sentError) {
        console.error("Error fetching sent notifications:", sentError);
        return;
      }

      const processedReceived = await Promise.all(
        (receivedData || []).map(async (notification) => {
          let senderProfile = null;
          let workspace = null;

          // Fetch sender profile if sender_id exists
          if (notification.sender_id) {
            try {
              const { data: profileData, error } = await supabase
                .from("profiles")
                .select("*")
                .eq("id", notification.sender_id)
                .maybeSingle();

              if (error) {
                console.error("Error fetching sender profile:", error);
              } else {
                senderProfile = profileData;
              }
            } catch (err) {
              console.error("Exception in sender profile fetch:", err);
            }
          }

          // Fetch workspace data using workspace_id field first, then fallback to content
          const workspaceId =
            notification.workspace_id || notification.content?.workspace_id;
          if (notification.type === "workspace_invitation" && workspaceId) {
            try {
              const { data: workspaceData, error } = await supabase
                .from("workspaces")
                .select("*")
                .eq("id", workspaceId)
                .maybeSingle();

              if (error) {
                console.error("Error fetching workspace:", error);
              } else {
                workspace = workspaceData;
              }
            } catch (err) {
              console.error("Exception in workspace fetch:", err);
            }
          }

          return {
            ...notification,
            direction: "received",
            sender_display_name:
              senderProfile?.full_name ||
              senderProfile?.username ||
              "Unknown User",
            sender_avatar_url: senderProfile?.avatar_url || null,
            workspace,
          };
        })
      );

      const processedSent = await Promise.all(
        (sentData || []).map(async (notification) => {
          let receiverProfile = null;
          let workspace = null;

          // Fetch recipient profile using receiver_id (primary) or user_id (fallback)
          const recipientId = notification.receiver_id || notification.user_id;
          if (recipientId) {
            try {
              const { data: profileData, error } = await supabase
                .from("profiles")
                .select("*")
                .eq("id", recipientId)
                .maybeSingle();

              if (error) {
                console.error("Error fetching receiver profile:", error);
              } else {
                receiverProfile = profileData;
              }
            } catch (err) {
              console.error("Exception in receiver profile fetch:", err);
            }
          }

          // Fetch workspace data using workspace_id field first, then fallback to content
          const workspaceId =
            notification.workspace_id || notification.content?.workspace_id;
          if (workspaceId) {
            try {
              const { data: workspaceData, error } = await supabase
                .from("workspaces")
                .select("*")
                .eq("id", workspaceId)
                .maybeSingle();

              if (error) {
                console.error("Error fetching workspace:", error);
              } else {
                workspace = workspaceData;
              }
            } catch (err) {
              console.error("Exception in workspace fetch:", err);
            }
          }

          return {
            ...notification,
            direction: "sent",
            receiver_display_name:
              receiverProfile?.full_name ||
              receiverProfile?.username ||
              "Unknown User",
            receiver_avatar_url: receiverProfile?.avatar_url || null,
            workspace,
          };
        })
      );

      const allNotifications = [...processedReceived, ...processedSent].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      const unreadCount = processedReceived.filter((n) => !n.is_read).length;

      setReceivedNotifications(processedReceived);
      setSentInvitations(processedSent);

      if (activeTab === "pending") {
        const pendingReceived = processedReceived.filter(
          (notification) => notification.status === "pending"
        );
        // Include all sent invitations in pending tab (they are all pending by nature)
        setFilteredNotifications([...pendingReceived, ...processedSent]);
      } else {
        setFilteredNotifications(processedReceived);
      }

      setUnreadCount(unreadCount);
    } catch (error) {
      console.error("Error processing notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptInvitation = async (notification) => {
    if (processingNotificationId) return;

    setProcessingNotificationId(notification.id);
    setIsAccepting(notification.id);
    try {
      const result = await acceptWorkspaceInvitation(notification.id, user.id);

      setReceivedNotifications(
        receivedNotifications.map((n) =>
          n.id === notification.id
            ? { ...n, status: "accepted", updated_at: new Date().toISOString() }
            : n
        )
      );

      const workspaceId =
        notification.workspace?.id || notification.content?.workspace_id;
      const workspaceName =
        notification.workspace?.name ||
        notification.content?.workspace_name ||
        "the workspace";

      if (!workspaceId) {
        console.error(
          "Could not determine workspace ID from notification:",
          notification
        );
        throw new Error("Missing workspace ID in invitation");
      }

      try {
        await useAuthStore
          .getState()
          .addWorkspaceMember(workspaceId, user.id, "member");
      } catch (memberError) {
        console.error("Error adding user as workspace member:", memberError);
      }

      try {
        const { data: currentProfile, error: profileError } = await supabase
          .from("profiles")
          .select("user_workspaces")
          .eq("id", user.id)
          .maybeSingle();

        if (profileError) {
          console.error("Error fetching user profile:", profileError);
        } else {
          let userWorkspaces = [];
          if (currentProfile.user_workspaces) {
            if (typeof currentProfile.user_workspaces === "string") {
              try {
                userWorkspaces = JSON.parse(currentProfile.user_workspaces);
              } catch (e) {
                console.error("Error parsing user_workspaces:", e);
                userWorkspaces = [];
              }
            } else if (Array.isArray(currentProfile.user_workspaces)) {
              userWorkspaces = currentProfile.user_workspaces;
            }
          }

          if (!userWorkspaces.includes(workspaceId)) {
            userWorkspaces.push(workspaceId);
            const { error: updateError } = await supabase
              .from("profiles")
              .update({
                user_workspaces: userWorkspaces,
                updated_at: new Date().toISOString(),
              })
              .eq("id", user.id);

            if (updateError) {
              console.error("Error updating user_workspaces:", updateError);
            }
          }
        }
      } catch (profileUpdateError) {
        console.error("Error updating user_workspaces:", profileUpdateError);
      }

      const updatedWorkspace =
        await SupabaseWorkspaceUtils.getWorkspace(workspaceId);

      if (!workspaces.some((w) => w.id === updatedWorkspace.id)) {
        setWorkspaces([...workspaces, updatedWorkspace]);
      } else {
        setWorkspaces(
          workspaces.map((w) =>
            w.id === updatedWorkspace.id
              ? {
                  ...w,
                  workspace_members: updatedWorkspace.workspace_members,
                }
              : w
          )
        );
      }

      try {
        const authStore = useAuthStore.getState();
        const currentProfile = authStore.profile;

        if (currentProfile) {
          let userWorkspaces = currentProfile.user_workspaces || [];

          if (typeof userWorkspaces === "string") {
            try {
              userWorkspaces = JSON.parse(userWorkspaces);
            } catch (e) {
              userWorkspaces = [];
            }
          } else if (!Array.isArray(userWorkspaces)) {
            userWorkspaces = [];
          }

          if (!userWorkspaces.includes(workspaceId)) {
            userWorkspaces.push(workspaceId);

            authStore.setProfile({
              ...currentProfile,
              user_workspaces: userWorkspaces,
            });
          }
        }
      } catch (authStoreError) {
        console.error("Error updating auth store profile:", authStoreError);
      }
    } catch (error) {
      console.error("Error accepting invitation:", error);
      toast({
        title: "Error",
        description:
          error.message || "Failed to accept invitation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessingNotificationId(null);
      setIsAccepting(null);
    }
  };

  const handleDeclineInvitation = async (notification) => {
    if (processingNotificationId) return;

    setProcessingNotificationId(notification.id);
    setIsDeclining(notification.id);
    try {
      await declineWorkspaceInvitation(notification.id, user.id);

      setReceivedNotifications(
        receivedNotifications.map((n) =>
          n.id === notification.id
            ? { ...n, status: "declined", updated_at: new Date().toISOString() }
            : n
        )
      );

      toast({
        title: "Invitation Declined",
        description: "The workspace invitation has been declined.",
      });
    } catch (error) {
      console.error("Error declining invitation:", error);
      toast({
        title: "Error",
        description: "Failed to decline invitation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessingNotificationId(null);
      setIsDeclining(null);
    }
  };

  const handleMarkAsRead = async (notification) => {
    if (processingNotificationId) return;

    setProcessingNotificationId(notification.id);
    try {
      const admin = getAdminClient();

      if (!admin || typeof admin.from !== "function") {
        console.error(
          "Invalid Supabase admin client. Using regular client as fallback."
        );
        const { error } = await supabase
          .from("notifications")
          .update({ is_read: true, updated_at: new Date().toISOString() })
          .eq("id", notification.id);

        if (error) throw error;
      } else {
        const { error } = await admin
          .from("notifications")
          .update({ is_read: true, updated_at: new Date().toISOString() })
          .eq("id", notification.id);

        if (error) throw error;
      }

      setReceivedNotifications(
        receivedNotifications.map((n) =>
          n.id === notification.id
            ? { ...n, is_read: true, updated_at: new Date().toISOString() }
            : n
        )
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
    } finally {
      setProcessingNotificationId(null);
    }
  };

  const getInitials = (name) => {
    if (!name) return "??";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const getNotificationIcon = (notification) => {
    let icon = <BellIcon className="h-5 w-5 text-blue-500" />;

    switch (notification.type) {
      case "workspace_invitation":
        if (notification.direction === "sent") {
          icon = <PaperAirplaneIcon className="h-5 w-5 text-blue-500" />;
        } else {
          icon = null;
        }
        break;
      case "message":
        icon = <ChatBubbleIcon className="h-5 w-5 text-green-500" />;
        break;
      case "system":
        icon = <InformationCircleIcon className="h-5 w-5 text-purple-500" />;
        break;
      case "alert":
        icon = <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
        break;
      case "error":
        icon = <ExclamationCircleIcon className="h-5 w-5 text-red-500" />;
        break;
      default:
    }

    return icon;
  };

  const renderNotificationContent = (notification) => {
    // Handle system notifications (e.g., stream completions)
    if (notification.type === "system") {
      const isStreamCompletion =
        notification.content?.action === "stream-completed";
      return (
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">
            {notification.content?.title || "System Notification"}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {notification.content?.message || ""}
          </p>
          {isStreamCompletion && notification.content?.threadName && (
            <p className="text-xs text-purple-600 dark:text-purple-400 mt-2 font-medium flex items-center gap-1">
              <svg
                className="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
              Click to view &quot;{notification.content.threadName}&quot;
            </p>
          )}
        </div>
      );
    }

    if (
      notification.direction === "sent" &&
      notification.type === "workspace_invitation"
    ) {
      return (
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">
            Workspace invitation sent to{" "}
            <span className="font-semibold">
              {notification.receiver_display_name || "Unknown User"}
            </span>
          </p>
          {notification.workspace && (
            <p className="text-sm text-muted-foreground">
              {notification.workspace.name || "Unknown Workspace"}
            </p>
          )}
        </div>
      );
    }

    if (
      notification.type === "workspace_invitation" &&
      notification.direction === "received"
    ) {
      return (
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">
            <span className="font-semibold">
              {notification.sender_display_name}
            </span>{" "}
            invited you
          </p>
          {notification.workspace && (
            <p className="text-sm text-muted-foreground">
              {notification.workspace.name || "Unknown Workspace"}
            </p>
          )}
          {notification.status === "pending" && (
            <div className="mt-3 flex space-x-2">
              <Button
                size="sm"
                variant="default"
                onClick={() => handleAcceptInvitation(notification)}
                disabled={isAccepting === notification.id}
                className="bg-white text-gray-900 hover:bg-gray-100 border border-gray-200 dark:bg-zinc-800 dark:text-white dark:hover:bg-zinc-700 dark:border-zinc-700"
              >
                {isAccepting === notification.id ? (
                  <Spinner className="h-4 w-4" />
                ) : (
                  "Accept"
                )}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleDeclineInvitation(notification)}
                disabled={isDeclining === notification.id}
                className="bg-red-500 hover:bg-red-600 text-white border-red-400 dark:bg-red-600 dark:hover:bg-red-700 dark:border-red-500"
              >
                {isDeclining === notification.id ? (
                  <Spinner className="h-4 w-4" />
                ) : (
                  "Decline"
                )}
              </Button>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">
          {notification.content?.message ||
            `${notification.type.charAt(0).toUpperCase() + notification.type.slice(1)} notification`}
        </p>
        {notification.sender_display_name &&
          notification.sender_display_name !== "Unknown User" && (
            <p className="text-sm text-muted-foreground">
              From: {notification.sender_display_name}
            </p>
          )}
      </div>
    );
  };

  const getPendingCount = () => {
    const pendingReceived = receivedNotifications.filter(
      (notification) => notification.status === "pending"
    ).length;

    return pendingReceived + sentInvitations.length;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[90vw] max-h-[90vh] w-[800px] h-[600px] bg-white/80 dark:bg-zinc-900/80 backdrop-blur-md border border-border/50 text-white p-0 gap-0 rounded-2xl flex flex-col overflow-hidden"
        hideCloseButton={true}
        style={{ zIndex: 9999 }}
      >
        <DialogTitle className="sr-only">Notifications</DialogTitle>
        <DialogDescription className="sr-only">
          View and manage your notifications
        </DialogDescription>

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-zinc-800 bg-transparent flex-shrink-0 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Notifications
            </h2>
            {getPendingCount() > 0 && (
              <Badge
                variant="secondary"
                className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
              >
                {getPendingCount()} new
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {filteredNotifications.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  filteredNotifications.forEach((notification) => {
                    if (
                      !notification.is_read &&
                      notification.direction !== "sent"
                    ) {
                      handleMarkAsRead(notification);
                    }
                  });
                }}
                disabled={isLoading}
                className="h-8 px-3 border-gray-300 dark:border-zinc-600 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              >
                Mark all read
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              className="h-8 w-8 bg-transparent hover:bg-transparent text-gray-900 dark:text-white hover:text-gray-900 dark:hover:text-white border-none"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-zinc-900 rounded-b-2xl">
          <div className="flex-1 overflow-auto">
            <div className="p-4">
              <Tabs
                value={activeTab}
                onValueChange={setActiveTab}
                className="w-full"
              >
                <div className="mb-4">
                  <TabsList className="grid w-full grid-cols-3 gap-1 bg-gray-100 dark:bg-zinc-800/80 p-1 rounded-lg">
                    <TabsTrigger
                      value="all"
                      className="rounded-md bg-white dark:bg-zinc-800 hover:bg-gray-50 dark:hover:bg-zinc-700 data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700 text-gray-700 dark:text-gray-300 data-[state=active]:text-gray-900 dark:data-[state=active]:text-white data-[state=active]:shadow-sm"
                    >
                      All
                    </TabsTrigger>
                    <TabsTrigger
                      value="pending"
                      className="rounded-md bg-white dark:bg-zinc-800 hover:bg-gray-50 dark:hover:bg-zinc-700 data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700 text-gray-700 dark:text-gray-300 data-[state=active]:text-gray-900 dark:data-[state=active]:text-white data-[state=active]:shadow-sm"
                    >
                      Pending
                      {getPendingCount() > 0 && (
                        <span className="ml-1.5 bg-purple-500 text-white text-xs rounded-full w-5 h-5 inline-flex items-center justify-center">
                          {getPendingCount()}
                        </span>
                      )}
                    </TabsTrigger>
                    <TabsTrigger
                      value="system"
                      className="rounded-md bg-white dark:bg-zinc-800 hover:bg-gray-50 dark:hover:bg-zinc-700 data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700 text-gray-700 dark:text-gray-300 data-[state=active]:text-gray-900 dark:data-[state=active]:text-white data-[state=active]:shadow-sm"
                    >
                      System
                    </TabsTrigger>
                  </TabsList>
                </div>

                <div className="pb-6 max-h-[60vh] overflow-y-auto">
                  <TabsContent value={activeTab} className="m-0 p-0">
                    {isLoading ? (
                      <div className="text-center py-10 px-4">
                        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                          <Spinner className="h-8 w-8 text-primary" />
                        </div>
                        <p className="text-foreground font-medium">
                          Loading notifications...
                        </p>
                      </div>
                    ) : filteredNotifications.length === 0 ? (
                      <div className="text-center py-10 px-4">
                        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                          <ArtifactLogo className="h-10 w-10" />
                        </div>
                        <p className="text-foreground font-medium">
                          No notifications found
                        </p>
                        <p className="text-muted-foreground text-sm mt-1">
                          {activeTab === "all"
                            ? "You don't have any notifications yet"
                            : activeTab === "pending"
                              ? "You don't have any pending invitations"
                              : "No system notifications"}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {filteredNotifications.map((notification, index) => (
                          <motion.div
                            key={
                              notification.id + (notification.direction || "")
                            }
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.2, delay: index * 0.05 }}
                            className={cn(
                              "p-4 rounded-xl border",
                              notification.direction === "sent"
                                ? "bg-blue-50/80 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800/30"
                                : notification.type === "system"
                                  ? notification.content?.action ===
                                    "stream-completed"
                                    ? "bg-purple-50/80 dark:bg-purple-900/10 border-purple-200 dark:border-purple-800/30 shadow-sm cursor-pointer hover:shadow-md transition-all"
                                    : "bg-background border-border"
                                  : notification.type ===
                                        "workspace_invitation" &&
                                      notification.status === "accepted"
                                    ? "bg-black/5 dark:bg-black/20 border-gray-200 dark:border-gray-700"
                                    : notification.type ===
                                          "workspace_invitation" &&
                                        notification.status === "pending"
                                      ? "bg-purple-50/80 dark:bg-purple-900/10 border-purple-200 dark:border-purple-800/30 shadow-sm"
                                      : "bg-background border-border"
                            )}
                            onClick={() => {
                              // Handle system notifications with navigation
                              if (
                                notification.type === "system" &&
                                notification.content?.action ===
                                  "stream-completed" &&
                                notification.content?.threadId
                              ) {
                                onOpenChange(false);
                                navigate(
                                  `/chat/${notification.content.threadId}`
                                );
                                if (!notification.is_read) {
                                  handleMarkAsRead(notification);
                                }
                                return;
                              }

                              if (
                                !notification.is_read &&
                                notification.direction !== "sent"
                              ) {
                                handleMarkAsRead(notification);
                              }
                            }}
                          >
                            <div className="flex items-start space-x-4">
                              <Avatar
                                className={cn(
                                  "h-10 w-10 border-2",
                                  notification.type === "system"
                                    ? "border-purple-200 dark:border-purple-700"
                                    : notification.direction === "sent"
                                      ? "border-blue-200 dark:border-blue-700"
                                      : notification.type ===
                                            "workspace_invitation" &&
                                          notification.status === "accepted"
                                        ? "border-gray-300 dark:border-gray-600"
                                        : "border-purple-200 dark:border-purple-700"
                                )}
                              >
                                {notification.type === "system" ? (
                                  <AvatarFallback className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white">
                                    <BellIcon className="h-5 w-5" />
                                  </AvatarFallback>
                                ) : (
                                  <>
                                    <AvatarImage
                                      src={
                                        notification.direction === "sent"
                                          ? notification.receiver_avatar_url
                                          : notification.sender_avatar_url
                                      }
                                      alt={
                                        notification.direction === "sent"
                                          ? notification.receiver_display_name
                                          : notification.sender_display_name
                                      }
                                    />
                                    <AvatarFallback
                                      className={
                                        notification.direction === "sent"
                                          ? "bg-gradient-to-br from-blue-500 to-indigo-600 text-white"
                                          : notification.type ===
                                                "workspace_invitation" &&
                                              notification.status === "accepted"
                                            ? "bg-gradient-to-br from-gray-700 to-black text-white"
                                            : "bg-gradient-to-br from-purple-500 to-indigo-600 text-white"
                                      }
                                    >
                                      {getInitials(
                                        notification.direction === "sent"
                                          ? notification.receiver_display_name
                                          : notification.sender_display_name
                                      )}
                                    </AvatarFallback>
                                  </>
                                )}
                              </Avatar>

                              <div className="flex-1 min-w-0">
                                {renderNotificationContent(notification)}
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </div>
              </Tabs>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationsDialog;
